/**
 * Phone verification (Firebase Phone Auth) state.
 *
 * Drives the "verify your phone" flow: enter number → send OTP → enter code → confirm. On success the
 * verified phone is linked to the user's Firebase Auth record, which is the source of truth the
 * inspiration webhook resolves via `getUserByPhoneNumber` (see `server/src/webhooks/CLAUDE.md`).
 *
 * This store is platform-neutral (ELARA-730): the app-verification mechanism (web invisible
 * reCAPTCHA; React Native native phone auth) lives entirely behind the injected
 * {@link PhoneVerificationPort}, so the state never imports DOM/web APIs. Web renders the flow in a modal
 * (ELARA-1169) and React Native in a bottom sheet, both driven by {@link isSheetOpen} — the
 * `openSheet`/`closeSheet` controls let any screen (Profile, and the blocking prompt for members who must
 * add a number, ELARA-1170) present the same flow. A verified number can be changed but not removed:
 * every member needs one.
 *
 * After a successful link it asks the server to text the number a welcome from the Elara number
 * (ELARA-1167). The server sends that at most once per account, so calling it on every link is safe.
 * `addElaraToContacts` opens Elara's contact card so that number shows up as "Elara" (ELARA-1178),
 * through the injected {@link ExternalUrlPort}.
 */

import type { AppFlowsContainer } from '#flows.js';
import type { ExternalUrlPort } from '#lib/external-url-port.js';
import type { PhoneVerificationChallenge, PhoneVerificationPort } from '#lib/phone-verification-port.js';
import type { SessionState } from './SessionState.js';

import { toE164 } from '@elara/core/utils';
import { ELARA_CONTACT_VCARD_PATH } from '@elara/domain/constants';

import { getEnv } from '#env-schema.js';
import {
  trackElaraContactCardOpened,
  trackPhoneVerificationCompleted,
  trackPhoneVerificationStarted,
} from '#lib/analytics-events.js';
import { toAbsolutePath } from '#lib/flow-utils.js';
import { createState } from '#lib/signals-utils.js';

/** Steps of the verification control. */
export type PhoneLinkStatus =
  | 'idle' // showing the verified number, or nothing to do
  | 'editing' // entering/changing a number
  | 'sendingCode' // startVerification in flight
  | 'awaitingCode' // code sent, waiting for the user to enter it
  | 'verifying' // confirming the code
  | 'verified'; // just linked this session

/** Map Firebase phone-auth error codes to friendly, non-enumerating messages. */
function phoneAuthErrorMessage(error: unknown): string {
  const code = (error as { code?: string } | null)?.code;
  switch (code) {
    case 'auth/invalid-phone-number':
    case 'auth/missing-phone-number': {
      return 'Enter a valid phone number, including the country code.';
    }
    case 'auth/invalid-verification-code': {
      return 'That code is incorrect. Check it and try again.';
    }
    case 'auth/code-expired': {
      return 'That code expired. Request a new one.';
    }
    case 'auth/too-many-requests': {
      return 'Too many attempts. Please try again in a little while.';
    }
    case 'auth/credential-already-in-use':
    case 'auth/account-exists-with-different-credential':
    case 'auth/phone-number-already-exists': {
      return 'This number is already linked to another account. Contact support.';
    }
    case 'auth/captcha-check-failed': {
      return 'Verification check failed. Please try again.';
    }
    default: {
      const message = (error as { message?: string } | null)?.message;
      return typeof message === 'string' && message.length > 0
        ? message
        : 'Could not verify your phone. Please try again.';
    }
  }
}

export function createPhoneLinkState(
  phoneVerification: PhoneVerificationPort,
  sessionState: SessionState,
  flows: Pick<AppFlowsContainer, 'sendSmsWelcome'>,
  externalUrl: ExternalUrlPort,
) {
  // Non-reactive handles kept in the closure (not signals): the in-flight verification challenge and
  // the exact E.164 we sent to (used to record the verified number after `confirm`, so it matches what
  // Firebase stored and what the webhook resolves — not the display-formatted input).
  let challenge: PhoneVerificationChallenge | undefined;
  let linkedE164: string | undefined;

  /**
   * Ask the server to text the newly verified number a welcome. Async so that ANY failure, including a
   * synchronous throw while starting the call, lands in this catch: the member is verified either way,
   * and the welcome must never turn that into an error.
   */
  const requestSmsWelcome = async (): Promise<void> => {
    try {
      await flows.sendSmsWelcome({});
    } catch (error) {
      console.error('[PhoneLinkState] sendSmsWelcome failed', error);
    }
  };

  return createState({
    status: 'idle' as PhoneLinkStatus,
    error: undefined as string | undefined,
    /** The number being entered/verified. */
    pendingPhone: '',
    /**
     * The phone verified during THIS session, and the account that verified it. Read first by
     * `verifiedPhone` for immediate UI feedback, since the `SessionState.user` signal only re-emits on
     * the next id-token event. Keyed by uid because this store outlives sign-out: without it, the next
     * account to sign in on the device would read as verified with the last one's number, and pass the
     * phone requirement (ELARA-1170).
     */
    sessionVerified: undefined as { uid: string; phone: string } | undefined,
    /**
     * Whether the verification flow is presented (the web modal or the React Native sheet). Lets any
     * screen open the shared flow via `openSheet()`.
     */
    isSheetOpen: false,

    /** The verified phone to display: this-session result first, else the live Firebase Auth user. */
    get verifiedPhone(): string | undefined {
      const user = sessionState.user;
      if (!user) return undefined;
      if (this.sessionVerified?.uid === user.uid) return this.sessionVerified.phone;
      return user.phoneNumber ?? undefined;
    },
    get isVerified(): boolean {
      return !!this.verifiedPhone;
    },
    get isBusy(): boolean {
      return this.status === 'sendingCode' || this.status === 'verifying';
    },

    setPendingPhone(value: string) {
      this.pendingPhone = value;
      this.error = undefined;
    },

    /** Open the editor to add or change a number. */
    beginEdit() {
      this.pendingPhone = '';
      this.error = undefined;
      this.status = 'editing';
    },

    /** Abandon the in-progress flow and return to the resting state. */
    cancel() {
      challenge = undefined;
      this.pendingPhone = '';
      this.error = undefined;
      this.status = this.isVerified ? 'verified' : 'idle';
    },

    /**
     * Present the modal/sheet flow. Opens the manage view (change / remove) when a
     * number is already verified, otherwise straight into the editor.
     */
    openSheet() {
      this.error = undefined;
      this.pendingPhone = '';
      this.isSheetOpen = true;
      this.status = this.isVerified ? 'idle' : 'editing';
    },

    /** Dismiss the modal/sheet flow and reset the in-progress state. */
    closeSheet() {
      this.isSheetOpen = false;
      this.cancel();
    },

    /**
     * Open Elara's contact card (a vCard from the server) so the member can save the Elara number with
     * its name and photo. The phone shows its own New Contact screen; nothing is saved without them.
     */
    async addElaraToContacts() {
      // Same base as the flow calls: absent on web (same origin), the Cloud Run URL on mobile.
      const baseUrl = getEnv().VITE_FLOW_API_BASE_URL?.trim() || undefined;
      await externalUrl.open(toAbsolutePath(ELARA_CONTACT_VCARD_PATH, baseUrl));
      trackElaraContactCardOpened();
    },

    /** Step 1 — send the OTP via the platform verification port. */
    async sendCode() {
      const e164 = toE164(this.pendingPhone);
      if (e164.length === 0) {
        this.error = 'Enter a phone number.';
        return;
      }
      this.status = 'sendingCode';
      this.error = undefined;
      try {
        challenge = await phoneVerification.startVerification(e164);
        linkedE164 = e164;
        this.status = 'awaitingCode';
        trackPhoneVerificationStarted();
      } catch (error) {
        this.error = phoneAuthErrorMessage(error);
        this.status = 'editing';
        console.error('[PhoneLinkState] sendCode failed', error);
      }
    },

    /** Step 2 — confirm the OTP, finishing the link. */
    async confirmCode(code: string) {
      if (!challenge) {
        this.error = 'Request a code first.';
        return;
      }
      if (code.trim().length === 0) {
        this.error = 'Enter the code we sent you.';
        return;
      }
      this.status = 'verifying';
      this.error = undefined;
      try {
        await challenge.confirm(code.trim());
        trackPhoneVerificationCompleted();
        // Record the exact E.164 we verified (not the display-formatted input), so it matches what
        // Firebase stored and what the webhook resolves.
        const uid = sessionState.user?.uid;
        if (uid && linkedE164) this.sessionVerified = { uid, phone: linkedE164 };
        challenge = undefined;
        this.status = 'verified';
        // The phone is now on the account; refresh the id token + __session cookie so downstream state
        // reflects it. Non-blocking.
        void sessionState.refreshCustomClaims(true);
        // Introduce the text channel on the newly verified number. Non-blocking: the send can take
        // seconds, and the member has already verified whatever happens to the text.
        void requestSmsWelcome();
      } catch (error) {
        this.error = phoneAuthErrorMessage(error);
        this.status = 'awaitingCode';
        console.error('[PhoneLinkState] confirmCode failed', error);
      }
    },
  });
}

export type PhoneLinkState = ReturnType<typeof createPhoneLinkState>;
